import type {
  NotificationListResponse,
  RealtimeNotificationInsert,
  StudentNotification,
} from './notifications-types'

/**
 * Pure notification list/badge state. `is_seen` and `is_read` are independent
 * server flags with independent timestamps; the badge shows `unseenCount`.
 * Every transition derives count deltas from the previous flags so applying a
 * server echo twice can never double-decrement, and counts never go negative.
 */
export interface NotificationsState {
  notifications: StudentNotification[]
  unreadCount: number
  unseenCount: number
  hasMore: boolean
}

export function emptyNotificationsState(): NotificationsState {
  return { notifications: [], unreadCount: 0, unseenCount: 0, hasMore: false }
}

export function fromListResponse(response: NotificationListResponse): NotificationsState {
  return {
    notifications: response.notifications,
    unreadCount: response.unread_count,
    unseenCount: response.unseen_count,
    hasMore: response.has_more,
  }
}

export function sameNotificationId(left: string, right: string) {
  return String(left) === String(right)
}

export function unseenIds(state: NotificationsState): string[] {
  return state.notifications.filter((notification) => !notification.is_seen).map((notification) => String(notification.id))
}

/**
 * Apply the echo of `PUT /notifications/<id>/read/`. Read implies seen on this
 * single-notification endpoint (the server sets both flags), so both counts
 * drop — but only for flags that were actually still set locally.
 */
export function applyMarkRead(state: NotificationsState, updated: StudentNotification): NotificationsState {
  const previous = state.notifications.find((notification) => sameNotificationId(notification.id, updated.id))
  if (!previous) return state
  return {
    ...state,
    notifications: state.notifications.map((notification) =>
      sameNotificationId(notification.id, updated.id) ? { ...notification, ...updated, id: String(updated.id) } : notification,
    ),
    unreadCount: !previous.is_read && updated.is_read ? Math.max(0, state.unreadCount - 1) : state.unreadCount,
    unseenCount: !previous.is_seen && updated.is_seen ? Math.max(0, state.unseenCount - 1) : state.unseenCount,
  }
}

/**
 * Apply the echo of `POST /notifications/batch_update/` with `mark_seen`.
 * Batch updates touch only the requested flag (unlike single mark-read), so
 * the echo is merged verbatim and only the unseen count moves.
 */
export function applyBatchSeen(state: NotificationsState, updated: StudentNotification[]): NotificationsState {
  const updatedById = new Map(updated.map((notification) => [String(notification.id), notification]))
  let seenDelta = 0
  const notifications = state.notifications.map((notification) => {
    const echo = updatedById.get(String(notification.id))
    if (!echo) return notification
    if (!notification.is_seen && echo.is_seen) seenDelta += 1
    return { ...notification, ...echo }
  })
  return { ...state, notifications, unseenCount: Math.max(0, state.unseenCount - seenDelta) }
}

/** Apply the success of `POST /notifications/mark_all_seen/` (account-wide). */
export function applyMarkAllSeen(state: NotificationsState): NotificationsState {
  return {
    ...state,
    notifications: state.notifications.map((notification) => ({ ...notification, is_seen: true })),
    unseenCount: 0,
  }
}

/**
 * Prepend a WebSocket-delivered notification. Delivery is at-least-once, so
 * an already-known ID is ignored entirely (no count change).
 */
export function insertRealtimeNotification(
  state: NotificationsState,
  insert: RealtimeNotificationInsert,
): NotificationsState {
  if (!insert.id || state.notifications.some((notification) => notification.id === insert.id)) return state
  const notification: StudentNotification = {
    id: insert.id,
    notification_type: 'grading_complete',
    message: insert.message,
    link: insert.link ?? null,
    is_read: insert.is_read ?? false,
    is_seen: insert.is_seen ?? false,
    created_at: insert.created_at ?? new Date().toISOString(),
  }
  return {
    ...state,
    notifications: [notification, ...state.notifications],
    unreadCount: state.unreadCount + (notification.is_read ? 0 : 1),
    unseenCount: state.unseenCount + (notification.is_seen ? 0 : 1),
  }
}
